/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box } from '@mui/material';
import React from 'react';
import { AlertBanner } from '@opengeoweb/shared';
import type {
  TafFromBackend,
  Taf,
  TafActions,
  TafStatus,
  TafFromFrontEnd,
} from '../../../types';
import { useTafTranslation } from '../../../utils/i18n';
import type { IssuesPanePosition } from '../../IssuesPane/types';
import Spinner from '../../Spinner/Spinner';
import { TabPanel } from '../../TabPanel';
import TafForm from '../../TafForm/TafForm';
import { getTafStatusLabel } from '../../TafForm/utils';
import TafHeader from '../../TafHeader/TafHeader';
import type { TafError } from '../utils';
import { isWarning } from '../utils';

export const fromActionToStatus = (tafAction: TafActions): TafStatus => {
  switch (tafAction) {
    case 'DRAFT':
      return 'DRAFT';
    case 'DRAFT_AMEND':
      return 'DRAFT_AMENDED';
    case 'DRAFT_CORRECT':
      return 'DRAFT_CORRECTED';
    case 'PUBLISH':
      return 'PUBLISHED';
    case 'AMEND':
      return 'AMENDED';
    case 'CORRECT':
      return 'CORRECTED';
    case 'CANCEL':
      return 'CANCELLED';
    default:
      return 'NEW';
  }
};

interface TafPanelProps {
  tafFromBackend: TafFromBackend;
  activeTafIndex: number;
  index: number;
  isLoading?: boolean;
  postTaf?: (taf: TafFromFrontEnd) => Promise<void>;
  patchTaf?: (taf: TafFromFrontEnd) => Promise<boolean>;
  isIssuesPaneOpen?: boolean;
  setIsIssuesPaneOpen?: (isOpen: boolean, position: IssuesPanePosition) => void;
  isFormDisabled?: boolean;
  setIsFormDisabled?: (isDisabled: boolean) => void;
  error?: TafError | null;
  previousTaf: Taf;
  tafAction: TafActions;
  shouldEnableRemark?: boolean;
}

const TafPanel: React.FC<TafPanelProps> = ({
  tafFromBackend,
  activeTafIndex,
  index,
  isLoading = false,
  postTaf = async (): Promise<void> => null!,
  patchTaf = async (): Promise<boolean> => null!,
  isIssuesPaneOpen = false,
  setIsIssuesPaneOpen = (): void => null!,
  isFormDisabled = false,
  setIsFormDisabled = (): void => null!,
  error,
  previousTaf = null!,
  tafAction,
  shouldEnableRemark = false,
}: TafPanelProps) => {
  const { t } = useTafTranslation();
  const severity = isWarning(error!) ? 'warning' : 'error';

  const onTafAction = async (
    action: TafActions,
    taf?: Taf,
  ): Promise<boolean> => {
    try {
      // Add right actions and their follow up calls to the BE
      switch (action) {
        case 'DRAFT':
        case 'DRAFT_AMEND':
        case 'DRAFT_CORRECT':
        case 'PUBLISH':
        case 'AMEND':
        case 'CANCEL':
        case 'CORRECT':
          // post taf
          await postTaf({
            taf: taf!,
            changeStatusTo: fromActionToStatus(action),
          });
          return true;
        default:
          return false;
      }
    } catch (_error) {
      return false;
    }
  };

  return (
    <TabPanel
      data-testid={`taf-panel${activeTafIndex === index ? '-active' : ''}`}
      index={index}
      value={activeTafIndex}
    >
      <TafHeader
        sx={{ marginBottom: '4px' }}
        title={getTafStatusLabel(
          t,
          tafFromBackend.taf.status,
          tafAction,
          isFormDisabled,
        )}
      />

      {isLoading ? (
        <Spinner
          data-testid="taf-loading"
          sx={{
            position: 'absolute',
            zIndex: 1,
            color: 'geowebColors.backdrops.white.fill',
          }}
        />
      ) : null}
      {error ? (
        <AlertBanner
          dataTestId="taf-error"
          info={error.message}
          severity={severity}
          shouldClose
          title={error.title}
        />
      ) : null}
      <Box sx={{ marginTop: { xs: '44px', sm: '10px' }, padding: 1 }}>
        <TafForm
          isDisabled={isFormDisabled}
          isIssuesPaneOpen={isIssuesPaneOpen}
          onFormAction={onTafAction}
          onSwitchEditor={patchTaf}
          previousTaf={previousTaf}
          setIsDisabled={setIsFormDisabled}
          setIsIssuesPaneOpen={setIsIssuesPaneOpen}
          shouldEnableRemark={shouldEnableRemark}
          tafFromBackend={tafFromBackend}
        />
      </Box>
    </TabPanel>
  );
};

export default TafPanel;
